import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class OAuthAuthService {
  private readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private readonly userClaimsSubject = new BehaviorSubject<Record<string, unknown> | null>(null);
  public userClaims$ = this.userClaimsSubject.asObservable();

  private isProcessingCallback = false;
  private readonly oauthService = inject(OAuthService);
  private readonly router = inject(Router);

  constructor() {
    this.setupAuth();
  }

  /**
   * Configura el servicio OAuth con la configuración proporcionada
   */
  async configure(config: AuthConfig): Promise<void> {
    try {
      this.validateConfig(config);
      this.oauthService.configure(config);

      try {
        await this.oauthService.loadDiscoveryDocument();
        this.updateAuthenticationStatus();
      } catch {
        this.updateAuthenticationStatus();
        return await Promise.resolve();
      }
    } catch {
      return;
    }
  }

  /**
   * Inicia el flujo de login redirigiendo a Cognito Hosted UI
   */
  login(targetUrl?: string): void {
    if (targetUrl) {
      sessionStorage.setItem('auth_redirect_url', targetUrl);
    }

    if (!this.oauthService.clientId) {
      return;
    }

    if (!this.oauthService.redirectUri) {
      return;
    }

    const redirectUri = this.oauthService.redirectUri;
    if (!redirectUri.startsWith('http://') && !redirectUri.startsWith('https://')) {
      return;
    }

    if (!this.oauthService.discoveryDocumentLoaded) {
      this.oauthService
        .loadDiscoveryDocument()
        .then(() => this.initiateLoginFlow())
        .catch(() => Promise.resolve());
      return;
    }

    this.initiateLoginFlow();
  }

  /**
   * Maneja el callback después del login
   * Debe ser llamado desde el componente de callback
   */
  handleCallback(): Observable<boolean> {
    if (this.isProcessingCallback) {
      return of(false);
    }
    this.isProcessingCallback = true;

    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const hashError = hashParams.get('error');

    if (error || hashError) {
      this.isProcessingCallback = false;
      return of(false);
    }

    const loginPromise = this.oauthService.discoveryDocumentLoaded
      ? this.oauthService.tryLogin()
      : this.oauthService.loadDiscoveryDocumentAndTryLogin();

    return from(loginPromise).pipe(
      map(() => {
        this.isProcessingCallback = false;

        if (this.oauthService.hasValidAccessToken()) {
          this.updateAuthenticationStatus();
          const redirectUrl = sessionStorage.getItem('auth_redirect_url') || '/dashboard';
          sessionStorage.removeItem('auth_redirect_url');

          this.router.navigateByUrl(redirectUrl).then(
            (success) => {
              if (!success) {
                window.location.href = redirectUrl;
              }
            },
            () => {
              window.location.href = redirectUrl;
            },
          );
          return true;
        }
        return false;
      }),
      catchError(() => {
        this.isProcessingCallback = false;
        this.router.navigate(['/login']);
        return of(false);
      }),
    );
  }

  /**
   * Cierra sesión y redirige al logout de Cognito
   * AWS Cognito requiere el parámetro 'logout_uri' en lugar del estándar OIDC
   */
  logout(): void {
    try {
      this.isAuthenticatedSubject.next(false);
      this.userClaimsSubject.next(null);

      const postLogoutRedirectUri =
        this.oauthService.postLogoutRedirectUri || window.location.origin + '/login';
      const clientId = this.oauthService.clientId;

      let logoutUrl: string | null = null;

      try {
        const oauthSvc = this.oauthService as OAuthService & {
          discoveryDocument?: { end_session_endpoint?: string };
          logoutUrl?: string;
        };
        const discoveryDoc = oauthSvc.discoveryDocument;
        if (discoveryDoc?.end_session_endpoint) {
          const endSessionEndpoint = discoveryDoc.end_session_endpoint;
          if (
            endSessionEndpoint &&
            endSessionEndpoint.includes('/logout') &&
            !endSessionEndpoint.includes('/login')
          ) {
            logoutUrl = endSessionEndpoint.split('?')[0];
          }
        }

        if (!logoutUrl && oauthSvc.logoutUrl) {
          const configuredUrl = oauthSvc.logoutUrl;
          if (configuredUrl.includes('/logout') && !configuredUrl.includes('/login')) {
            logoutUrl = configuredUrl;
          }
        }
      } catch {
        /* Ignorar */
      }

      if (!logoutUrl) {
        logoutUrl = 'https://us-east-2upo2y8t6a.auth.us-east-2.amazoncognito.com/logout';
      }

      if (logoutUrl && (logoutUrl.includes('/login') || !logoutUrl.includes('/logout'))) {
        logoutUrl = logoutUrl.replace('/login', '/logout');
        if (!logoutUrl.includes('/logout')) {
          logoutUrl = 'https://us-east-2upo2y8t6a.auth.us-east-2.amazoncognito.com/logout';
        }
      }

      let logoutUrlWithParams = `${logoutUrl}?client_id=${clientId}&logout_uri=${encodeURIComponent(
        postLogoutRedirectUri,
      )}`;

      if (logoutUrlWithParams.includes('/login')) {
        logoutUrlWithParams = logoutUrlWithParams.replace('/login', '/logout');
      }

      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes('token') ||
            key.includes('oauth') ||
            key.includes('auth') ||
            key.startsWith('angular-oauth') ||
            key.startsWith('oidc.'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      sessionStorage.clear();

      window.location.href = logoutUrlWithParams;
    } catch {
      try {
        this.oauthService.logOut();
      } catch {
        /* Ignorar */
      }

      localStorage.clear();
      sessionStorage.clear();
      this.isAuthenticatedSubject.next(false);
      this.userClaimsSubject.next(null);
      this.router.navigate(['/login']);
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    try {
      if (!this.oauthService || !this.oauthService.clientId) {
        return false;
      }
      return this.oauthService.hasValidAccessToken();
    } catch {
      return false;
    }
  }

  /**
   * Obtiene el access token actual
   */
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  /**
   * Obtiene el ID token actual
   */
  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  /**
   * Obtiene los claims del ID token (información del usuario)
   */
  getIdentityClaims(): Record<string, unknown> | null {
    return this.oauthService.getIdentityClaims();
  }

  /**
   * Obtiene un claim específico del usuario
   */
  getUserClaim(claim: string): string | null {
    const claims = this.getIdentityClaims();
    const value = claims ? claims[claim] : null;
    return typeof value === 'string' ? value : null;
  }

  /**
   * Obtiene el email del usuario
   */
  getUserEmail(): string | null {
    return this.getUserClaim('email') || this.getUserClaim('sub');
  }

  /**
   * Obtiene el nombre del usuario
   */
  getUserName(): string | null {
    return (
      this.getUserClaim('name') ||
      this.getUserClaim('given_name') ||
      this.getUserClaim('preferred_username')
    );
  }

  /**
   * Verifica si el token está próximo a expirar
   */
  isTokenExpiringSoon(): boolean {
    if (!this.isAuthenticated()) {
      return false;
    }

    const expiration = this.oauthService.getAccessTokenExpiration();
    if (!expiration) return false;

    // Considerar "próximo a expirar" si queda menos de 5 minutos
    const fiveMinutes = 5 * 60 * 1000;
    const timeUntilExpiration = expiration - Date.now();

    // Si el token ya expiró, forzar re-login
    if (timeUntilExpiration <= 0) {
      this.handleSessionTerminated();
      return true;
    }

    return timeUntilExpiration < fiveMinutes;
  }

  /**
   * Intenta refrescar el token
   */
  refreshToken(): Promise<void> {
    return this.oauthService.refreshToken().then(() => {
      this.updateAuthenticationStatus();
    });
  }

  /**
   * Valida la configuración antes de aplicarla
   */
  private validateConfig(config: AuthConfig): void {
    if (!config) {
      throw new Error('AuthConfig es requerido');
    }

    if (!config.issuer) {
      throw new Error('issuer es requerido en AuthConfig');
    }

    if (!config.clientId) {
      throw new Error('clientId es requerido en AuthConfig');
    }

    if (!config.redirectUri) {
      throw new Error('redirectUri es requerido en AuthConfig');
    }

    if (!config.redirectUri.startsWith('http://') && !config.redirectUri.startsWith('https://')) {
      throw new Error(
        'redirectUri debe ser una URL completa (debe comenzar con http:// o https://)',
      );
    }

    if (!config.issuer.startsWith('https://')) {
      // issuer debería usar HTTPS en producción
    }
  }

  /**
   * Inicializa la configuración y verifica si hay una sesión activa
   */
  private setupAuth(): void {
    this.oauthService.events.subscribe((event) => {
      if (event.type === 'token_received' || event.type === 'token_refreshed') {
        this.updateAuthenticationStatus();
      }
      if (event.type === 'session_terminated') {
        this.handleSessionTerminated();
      }
    });
    this.updateAuthenticationStatus();
  }

  /**
   * Inicia el flujo de login después de validar la configuración
   */
  private initiateLoginFlow(): void {
    try {
      this.oauthService.initCodeFlow();
    } catch {
      /* Error al iniciar flujo */
    }
  }

  /**
   * Actualiza el estado de autenticación y los claims del usuario
   */
  private updateAuthenticationStatus(): void {
    const isAuthenticated = this.isAuthenticated();
    this.isAuthenticatedSubject.next(isAuthenticated);

    if (isAuthenticated) {
      const claims = this.getIdentityClaims();
      this.userClaimsSubject.next(claims);
    } else {
      this.userClaimsSubject.next(null);
    }
  }

  /**
   * Maneja la terminación de sesión
   */
  private handleSessionTerminated(): void {
    this.isAuthenticatedSubject.next(false);
    this.userClaimsSubject.next(null);
    localStorage.clear();
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }
}
